import React, { useState, useEffect, useContext } from 'react';
import { db } from './firebase';
import {
  collection, getDocs, doc, getDoc, query, where, runTransaction, serverTimestamp
} from 'firebase/firestore';
import {
  Box, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Select, MenuItem, FormControl,
  InputLabel, Grid, Stack, IconButton, Snackbar, Alert, CircularProgress, Autocomplete, FormLabel, Avatar, Modal
} from '@mui/material';
import { Delete } from "@mui/icons-material";
import Layout from './Layout';
import AppContext from './AppContext';
import SaleSummaryPrint from './components/SaleSummaryPrint';

const SellPage = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState('');
  const [saleItems, setSaleItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const { shopId, userId, userName, saleTax, t } = useContext(AppContext);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);
  const [cashiers, setCashiers] = useState([]);
  const [selectedCashier, setSelectedCashier] = useState('');
  const [showSaleSummaryPrint, setShowSaleSummaryPrint] = useState(false);
  const [lastSaleDetails, setLastSaleDetails] = useState(null);
  const [cashierError, setCashierError] = useState(false);
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setIsOffline(!navigator.onLine);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!shopId) {
      return;
    }

    const fetchProducts = async () => {
      try {
        const productsQuery = query(
          collection(db, 'products'),
          where('productType', '==', 'Sale'),
          where('shopId', '==', shopId)
        );
        const querySnapshot = await getDocs(productsQuery);
        const productList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          stock: doc.data().stock || []
        }));
        setProducts(productList);
      } catch {
        console.log('Error loading data');
      }
    };
    fetchProducts();
  }, [shopId]);

  useEffect(() => {
    if (!shopId) {
      return;
    }

    const fetchCashiers = async () => {
      try {
        const shopRef = doc(db, 'shops', shopId);
        const shopSnap = await getDoc(shopRef);
        if (shopSnap.exists()) {
          const cashiers = shopSnap.data().cashiers || [];
          setCashiers(cashiers);
        }
      } catch {
        console.log(t("Error loading data"));
      }
    };
    fetchCashiers();
  }, [shopId]);

  const handleStockChange = (event) => {
    if (selectedProduct?.stock) {
      setSelectedStock(selectedProduct.stock.find(s => s.stockCode === event.target.value));
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCashierChange = (name) => {
    setSelectedCashier(name);
    if (cashierError) {
      setCashierError(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const addToSale = () => {
    if (!selectedProduct || !selectedStock || quantity <= 0) return;
    if (parseInt(quantity) > parseInt(selectedStock.quantity)) {
      showSnackbar('Quantity exceeds available stock!', 'warning');
      return;
    }
    const item = {
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      stockCode: selectedStock.stockCode,
      quantity: parseInt(quantity),
      price: parseFloat(price),
      total: parseInt(quantity) * parseFloat(price)
    };
    setSaleItems([...saleItems, item]);
    setQuantity(1);
  };

  const removeItem = (index) => {
    setSaleItems(saleItems.filter((_, i) => i !== index));
  };

  const subtotal = saleItems.reduce((sum, item) => sum + item.total, 0);
  const tax = subtotal * saleTax;
  const total = subtotal + tax;

  // Firestore transaction for atomic stock deduction and sale creation
  const confirmSale = async () => {
    if (selectedCashier === '') {
      setCashierError(true);
      return;
    }
    if (!shopId) {
      showSnackbar('Unable to process sale without a shop.', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const orderId = Number(Date.now().toString().slice(-6));
      const orderDate = new Date();
      const cashierName = selectedCashier || userName;
      const description = saleItems.map(item => `${item.productName} x ${item.quantity}`).join(', ');
      const cartItemsForPrint = saleItems.map(item => ({
        name: item.productName,
        selectedVariant: item.stockCode,
        quantity: item.quantity,
        price: item.price,
        variantPrice: 0,
        totalPrice: item.total
      }));

      let updatedProductsSnapshot = null;
      let saleDetailsForPrint = null;

      await runTransaction(db, async (transaction) => {
        const updatedProducts = [...products];
        const stockUpdates = [];

        for (const item of saleItems) {
          const productRef = doc(db, 'products', item.productId);
          const productSnap = await transaction.get(productRef);

          if (!productSnap.exists()) {
            throw new Error(`Product ${item.productName} not found or was deleted.`);
          }

          const productData = productSnap.data();
          const stockIndex = productData.stock.findIndex(s => s.stockCode === item.stockCode);

          if (stockIndex === -1) {
            throw new Error(
              `Stock code ${item.stockCode} not found for ${item.productName}. It may have been already sold.`
            );
          }

          const currentStock = Number(productData.stock[stockIndex].quantity);
          const quantityToDeduct = Number(item.quantity);

          if (isNaN(currentStock) || isNaN(quantityToDeduct)) {
            throw new Error(`Invalid quantity values for ${item.productName}`);
          }

          if (currentStock < quantityToDeduct) {
            throw new Error(
              `Insufficient stock for ${item.productName} (${item.stockCode}). Available: ${currentStock}`
            );
          }

          const newQuantity = currentStock - quantityToDeduct;
          const updatedStock = [...productData.stock];

          if (newQuantity === 0) {
            updatedStock.splice(stockIndex, 1);
          } else {
            updatedStock[stockIndex] = {
              ...productData.stock[stockIndex],
              quantity: newQuantity.toString()
            };
          }

          stockUpdates.push({
            ref: productRef,
            stock: updatedStock,
            productData: { ...productData, id: item.productId, stock: updatedStock }
          });
        }

        for (const update of stockUpdates) {
          transaction.update(update.ref, { stock: update.stock });

          const localProductIndex = updatedProducts.findIndex(p => p.id === update.productData.id);
          if (localProductIndex !== -1) {
            updatedProducts[localProductIndex] = update.productData;
          }
        }

        updatedProductsSnapshot = updatedProducts;

        const cashRecord = {
          amount: total,
          description,
          orderId,
          paymentMethod,
          shopId,
          timestamp: serverTimestamp(),
          transactionType: 'income',
          userId,
          userName
        };
        transaction.set(doc(collection(db, 'cashbook')), cashRecord);

        const saleRecord = {
          amount: total,
          description,
          orderId,
          paymentMethod,
          items: saleItems,
          shopId,
          timestamp: serverTimestamp(),
          transactionType: 'income',
          userId,
          cashierName,
          orderDate,
          subtotal,
          tax,
          paymentAmount: total
        };
        transaction.set(doc(collection(db, 'sales')), saleRecord);

        saleDetailsForPrint = {
          ...saleRecord,
          timestamp: orderDate,
          cartItems: cartItemsForPrint
        };
      });

      if (updatedProductsSnapshot) {
        setProducts(updatedProductsSnapshot);
      }

      if (saleDetailsForPrint) {
        setLastSaleDetails(saleDetailsForPrint);
        setShowSaleSummaryPrint(true);
      }
      setSaleItems([]);
      setSelectedProduct(null);
      setSelectedStock(null);
      setQuantity(1);
      setPrice('');
      setSelectedCashier('');
      setCashierError(false);
      showSnackbar('Sale completed successfully!', 'success');
    } catch (error) {
      console.error('Error confirming sale:', error);
      showSnackbar(error.message || 'Failed to process sale.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintComplete = () => {
    setShowSaleSummaryPrint(false);
    setLastSaleDetails(null);
  };

  const formatToMyanmarCurrency = (number) => {
    const formattedNumber = new Intl.NumberFormat('my-MM').format(number);
    return `${formattedNumber}`;
  };

  const isAddButtonDisabled = !selectedProduct || !selectedStock || quantity <= 0;

  const handleCloseSaleSummary = () => {
    setShowSaleSummaryPrint(false);
  };

  return (
    <Layout>
      <Box sx={{ p: 3, mt: 0 }}>
        {isOffline && (
          <Alert severity="warning" mt={1} mb={3}>
            {t("You are offline. Some features may not be available.")}
          </Alert>
        )}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Typography variant="h4" mb={2}>{t("New Sale")}</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              options={products}
              getOptionLabel={(option) => option.name || ''}
              value={selectedProduct}
              onChange={(event, newValue) => {
                setSelectedProduct(newValue);
                setSelectedStock(null);
                setPrice(newValue?.price || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Product")}
                  variant="outlined"
                  fullWidth
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </Grid>
          {selectedProduct && (
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>{t("Stock")}</InputLabel>
                <Select
                  value={selectedStock?.stockCode || ''}
                  label={t("Stock")}
                  onChange={handleStockChange}
                >
                  {!selectedProduct.stock || selectedProduct.stock.length === 0 ? (
                    <MenuItem value="" disabled>
                      No Stock Available
                    </MenuItem>
                  ) : (
                    [
                      <MenuItem key="select" value="">{t("Select Stock")}</MenuItem>,
                      ...selectedProduct.stock.map(stock => (
                        <MenuItem key={stock.stockCode} value={stock.stockCode}>
                          {stock.stockCode} - {stock.quantity} units
                        </MenuItem>
                      ))
                    ]
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="number"
              label={t("Quantity")}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              onClick={addToSale}
              fullWidth
              sx={{ height: '100%' }}
              disabled={isAddButtonDisabled}
            >
              {t("Add")}
            </Button>
          </Grid>
        </Grid>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("Product")}</TableCell>
                <TableCell>{t("Stock Code")}</TableCell>
                <TableCell>{t("Qty")}</TableCell>
                <TableCell>{t("Price")}</TableCell>
                <TableCell>{t("Amount")}</TableCell>
                <TableCell>{t("Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {saleItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">
                      {t("No items added to sale")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                saleItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell>{item.stockCode}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{formatToMyanmarCurrency(item.price)}</TableCell>
                    <TableCell>{formatToMyanmarCurrency(item.total)}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => removeItem(index)} color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box elevation={1} sx={{ mt: 3, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
          <Stack spacing={1} sx={{ textAlign: 'right' }}>
            <Typography>{t("Subtotal")}: {formatToMyanmarCurrency(subtotal)}</Typography>
            <Typography>{t("Tax")}: {formatToMyanmarCurrency(tax)}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {t("Total")}: {formatToMyanmarCurrency(total)}
            </Typography>
          </Stack>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <FormControl sx={{ mt: 2, minWidth: 120 }}>
              <InputLabel>{t("Payment Method")}</InputLabel>
              <Select value={paymentMethod} label={t("Payment Method")} onChange={(e) => setPaymentMethod(e.target.value)}>
                <MenuItem value="Cash">{t("Cash")}</MenuItem>
                <MenuItem value="Kpay">{t("Kpay")}</MenuItem>
                <MenuItem value="WavePay">{t("WavePay")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <FormLabel sx={{ textAlign: 'right' }}>{t("Cashier Name")}</FormLabel>
              <Box
                sx={{
                  display: 'flex',
                  overflowX: 'auto',
                  gap: 2,
                  py: 2,
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,.2)',
                    borderRadius: '4px',
                  },
                  justifyContent: 'flex-start',
                  flexDirection: 'row-reverse',
                  border: cashierError ? '1px solid red' : 'none',
                  borderRadius: cashierError ? 2 : 0,
                }}
              >
                {cashiers.map((name) => (
                  <Box
                    key={name}
                    onClick={() => handleCashierChange(name)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 100,
                      cursor: 'pointer',
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: selectedCashier === name ? 'grey.100' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        mb: 1,
                        fontSize: '1.5rem',
                        backgroundColor: selectedCashier === name ? 'primary.main' : 'grey.500',
                      }}
                      alt={name}
                    >
                      {name.charAt(0)}
                    </Avatar>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'center',
                        fontWeight: selectedCashier === name ? 'bold' : 'normal',
                      }}
                    >
                      {name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </FormControl>
          </Box>
        </Box>
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setSaleItems([])}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={confirmSale}
            disabled={saleItems.length === 0 || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? t("Processing...") : t("Confirm Sale")}
          </Button>
        </Stack>
      </Box>
      <Modal
        open={showSaleSummaryPrint}
        onClose={handleCloseSaleSummary}
        aria-labelledby="order-summary-modal"
        aria-describedby="order-summary-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: 800,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <SaleSummaryPrint
            saleDetails={lastSaleDetails}
            onClose={handlePrintComplete}
          />
        </Box>
      </Modal>
    </Layout>
  );
};

export default SellPage;
